import { createSelector } from "@reduxjs/toolkit";
import { ON_INVOICE_DISCOUNT_TYPE_CODE } from "@utils/Constants";
import { RootState } from "@/store";
import { DocumentSummaryProps, EDocumentState } from "../eDocumentSlides";
import { calculateDocumentGranTotals } from "./eDocumentUtils";

export type DocumentSummaryResult = DocumentSummaryProps & { impuestosExentos: number };

/**
 * Resumen del documento derivado del slice. Antes vivia como estado propio
 * (`eDocument.documentSummary`) y se sincronizaba a mano con dos
 * `dispatch(setDocumentSummary(...))` desde EdocumentProductList; cualquier cosa
 * que cambiara el total sin pasar por ese componente quedaba desincronizada.
 *
 * Se memoiza sobre el slice y no sobre RootState para poder usarse tambien con
 * el estado que devuelve `flushDocumentProductEdits()`, que es solo el slice.
 */
export const selectEDocumentSummary = createSelector(
    [
        (eDocument: EDocumentState) => eDocument.documentProducts,
        (eDocument: EDocumentState) => eDocument.documentDiscountType,
        (eDocument: EDocumentState) => eDocument.documentOtherCharges,
    ],
    (products, documentDiscountType, otherCharges): DocumentSummaryResult =>
        calculateDocumentGranTotals(
            products ?? [],
            documentDiscountType ?? ON_INVOICE_DISCOUNT_TYPE_CODE,
            { otherCharges },
        ),
);

/** Version para `useAppSelector` / `store.getState()`. */
export const selectDocumentSummary = (state: RootState): DocumentSummaryResult =>
    selectEDocumentSummary(state.eDocument);

/**
 * Venta neta del documento: la base sobre la que se resuelve un cargo porcentual.
 * Con CREDIT_NOTE_DISCOUNT_TYPE_CODE el descuento no viaja al comprobante, asi que
 * la base es el subtotal bruto.
 */
export const selectEDocumentVentaNeta = (eDocument: EDocumentState): number => {
    const summary = selectEDocumentSummary(eDocument);
    const discountType = eDocument.documentDiscountType ?? ON_INVOICE_DISCOUNT_TYPE_CODE;
    return discountType === ON_INVOICE_DISCOUNT_TYPE_CODE
        ? summary.subtotal - summary.descuento
        : summary.subtotal;
};

export const selectDocumentVentaNeta = (state: RootState): number =>
    selectEDocumentVentaNeta(state.eDocument);
